import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/Button'

export function GuestGate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { signInWithCode, loading } = useAuth()
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!input.trim()) return
    setError(null)
    setIsSubmitting(true)

    try {
      const userCredential = await signInWithCode(input)
      if (userCredential) {
        const idTokenResult = await userCredential.user.getIdTokenResult()
        if (idTokenResult.claims.role === 'admin') {
          navigate('/admin', { replace: true })
          return
        }
      }
    } catch (err: any) {
      console.error(err)
      if (err.code === 'auth/invalid-custom-token') {
        setError('System error: Invalid token. Please contact support.')
      } else if (err.message.includes('Invite code expired')) {
        setError(t('rsvp.code_error_invalid'))
      } else if (err.message.includes('Invite code already used')) {
        setError(t('rsvp.code_error_used'))
      } else {
        setError(t('rsvp.code_error_invalid'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Magic Link Support
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get('code');
    if (rawCode) {
      const code = rawCode.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      setInput(code);
      if (code.length >= 4) {
        setIsSubmitting(true);
        // Clear the code from the URL immediately
        window.history.replaceState({}, '', window.location.pathname);
        signInWithCode(code)
          .then(async (userCredential) => {
            if (userCredential) {
              const idTokenResult = await userCredential.user.getIdTokenResult()
              if (idTokenResult.claims.role === 'admin') {
                navigate('/admin', { replace: true })
                return
              }
            }
            navigate('/rsvp', { replace: true });
          })
          .catch((err: any) => {
            console.error("Auto-login failed", err);
            setError(t('rsvp.code_error_invalid'));
            setIsSubmitting(false);
          });
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-primary text-lg font-display tracking-widest uppercase animate-pulse">
          {t('common.loading')}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-secondary animate-fade-in relative overflow-hidden">
      <form
        className="relative z-10 w-full max-w-lg flex flex-col items-center gap-6"
        onSubmit={handleSubmit}
      >
        {/* Heading */}
        <div className="text-center space-y-4 w-full">
          <h1
            className="text-4xl md:text-[52px] font-serif text-primary italic font-light tracking-wide text-center whitespace-nowrap"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
          >
            {t('rsvp.gate_heading')}
          </h1>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-4 opacity-50">
            <span className="block w-16 h-px bg-primary-light" />
            <span className="text-primary-light text-sm">✦</span>
            <span className="block w-16 h-px bg-primary-light" />
          </div>
        </div>

        {/* Input area */}
        <div className="flex flex-col items-center gap-4 w-full">
          <div className="w-80 relative group">
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
              placeholder={t('rsvp.code_label')}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={isSubmitting}
              className="w-full py-4 px-8 bg-[#efe9d8]/95 border-2 border-brand-accent/40 rounded-full text-center text-3xl tracking-[0.4em] text-brand-accent placeholder:text-brand-accent/20 placeholder:text-lg placeholder:tracking-widest focus:outline-none focus:ring-4 focus:ring-primary/30 focus:border-primary shadow-2xl font-sans font-bold transition-all duration-300 group-hover:border-brand-accent/60"
              style={{ color: error ? 'var(--color-error)' : undefined }}
            />
          </div>

          {/* Error message — fixed height container to prevent layout shift */}
          <div className="h-6 flex items-center justify-center w-full">
            {error && (
              <p className="text-red-400 text-sm font-medium italic text-center drop-shadow-md animate-fade-in">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Submit button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={isSubmitting || !input.trim()}
          className="max-w-[320px] shadow-2xl shadow-primary/20 text-lg py-5"
        >
          {isSubmitting ? t('common.saving') : t('rsvp.enter_code')}
        </Button>
      </form>
    </div>
  )
}
